import React, { PropsWithChildren, useMemo } from 'react';
import { Box, Button, Divider, Flex, Pane, Pill, Text, Tooltip } from '@fullstory/components';
import { getNamedElementMetadata, getNamedElementName, isPendingSuggestion } from '../../../utils';
import { useSelector } from 'react-redux';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { fullstorySelectors } from '@fullstory/redux';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import { ObjectDetailsTooltipContent } from '@fullstory/dqm-ui';
import { intl } from '@fullstory/intl';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import css from './index.module.scss';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { FormattedMessage } from 'react-intl';
import EventSupport = DQMRow.EventSupport;
import { AISuggestedElementBadge } from '../../AISuggestedElementBadge';
import { useAiSuggestionsExploreEnabled } from '@fullstory/library-ui';
import { useNamedElementActivityData } from '../../../hooks/useNamedElementActivityData';

const platformLabels: { [key in NativePlatform]: string } = {
  [NativePlatform.NATIVE_PLATFORM_INVALID]: intl.formatMessage({
    defaultMessage: 'Invalid',
    id: '89T/ze',
  }),
  [NativePlatform.NATIVE_PLATFORM_WEB]: intl.formatMessage({ defaultMessage: 'Web', id: 'B6CB37' }),
  [NativePlatform.NATIVE_PLATFORM_IOS]: intl.formatMessage({ defaultMessage: 'iOS', id: 'OQDKnx' }),
  [NativePlatform.NATIVE_PLATFORM_ANDROID]: intl.formatMessage({
    defaultMessage: 'Android',
    id: 'gQ7kPU',
  }),
};

export interface NamedElementDetailsTooltipProps {
  namedElementId: string;
  // If the element is a pending suggestion, the id will be empty and instead pass this record
  suggElementRecord?: NamedElementRecord;
  onEdit?: () => void;
  className?: string;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
}

export const NamedElementDetailsTooltipContainer: React.FC<
  PropsWithChildren<NamedElementDetailsTooltipProps>
> = ({ onEdit, namedElementId, suggElementRecord }) => {
  const flags = useFlags();
  const canEdit = flags.hasBit(Flag.PermModifyNamedElements);
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);

  const { namedElement, activityMonitoringData, loading } = useNamedElementActivityData(
    namedElementId,
    suggElementRecord,
  );

  const aiSuggestionsExploreEnabled = useAiSuggestionsExploreEnabled();
  const shouldShowAIExplorationFeatures =
    aiSuggestionsExploreEnabled && isPendingSuggestion(namedElement?.namedElement);

  const platforms = useMemo(() => {
    const platformSelectors = namedElement?.namedElement?.platformSelectors;
    const set = new Set(platformSelectors?.map(p => p.platforms).flat());
    return [...set.values()].sort((a, b) => a - b).map(p => platformLabels[p]);
  }, [namedElement?.namedElement?.platformSelectors]);

  if (!namedElement || loading) {
    return null;
  }

  return (
    <ObjectDetailsTooltipContent
      data-component="NamedElementDetailsTooltipContainer"
      name={getNamedElementName(namedElement)}
      description={namedElement.namedElement?.description || ''}
      canEdit={canEdit}
      metadata={getNamedElementMetadata(namedElement)}
      activityMonitoring={activityMonitoringData}
      onEdit={shouldShowAIExplorationFeatures ? undefined : onEdit}
      isView={isPendingSuggestion(namedElement?.namedElement)}
      showDataDirect={showDataDirect}
      headerIcon={
        shouldShowAIExplorationFeatures ? { name: 'ai', color: 'ai', size: 20 } : undefined
      }
      objectType="Element"
      extraContent={
        <>
          <Flex justifyContent="space-between" alignItems="baseline">
            <Text appearance="bodySmall" color="emphasized">
              {intl.formatMessage({ defaultMessage: 'Selectors', id: 'HXe1UD' })}
            </Text>
            <Box display="flex" flexDirection="column" gap={4} width="80%">
              {namedElement?.namedElement?.platformSelectors.map((p, i) => (
                <Tooltip key={p.selector} tooltipContent={p.selector}>
                  <Text appearance="bodySmall" noWrap>
                    <Pill variant="primary">{p.selector}</Pill>
                  </Text>
                </Tooltip>
              ))}
            </Box>
          </Flex>
          <Divider className={css.divider} />
          <Flex justifyContent="space-between">
            <Text appearance="bodySmall" color="emphasized">
              {intl.formatMessage({ defaultMessage: 'Platforms', id: '6GGPoo' })}
            </Text>
            <Text appearance="bodySmall">
              {intl.formatList(platforms, { type: 'conjunction', style: 'short' })}
            </Text>
          </Flex>
        </>
      }
      footerContent={
        shouldShowAIExplorationFeatures ? (
          <Pane
            minHeight={66}
            padding={16}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            backgroundColor="secondary"
          >
            <AISuggestedElementBadge />
            <Button variant="fill-primary" onClick={onEdit} size="small">
              <FormattedMessage defaultMessage="Review" id="R+J5ox" />
            </Button>
          </Pane>
        ) : null
      }
      eventSupport={EventSupport.EVENT_SUPPORT_SUPPORTED}
    />
  );
};

export const NamedElementDetailsTooltip: React.FC<
  PropsWithChildren<NamedElementDetailsTooltipProps>
> = ({ namedElementId, suggElementRecord, onEdit, children, className, placement }) => (
  <Tooltip
    data-component="NamedElementDetailsTooltip"
    disablePadding
    placement={placement || 'right'}
    maxWidth={400}
    className={className}
    showDelay={300}
    persistDelay={100}
    tooltipContent={
      <NamedElementDetailsTooltipContainer
        namedElementId={namedElementId}
        suggElementRecord={suggElementRecord}
        onEdit={onEdit}
        key={namedElementId}
      />
    }
    variant="light"
    childrenOverflowHidden
  >
    {children}
  </Tooltip>
);
